import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import VideoModal from '../ui/VideoModal'
import { baseFileId, posterUrl } from '../ui/videoSource'
import {
  Confirm,
  Empty,
  IconOpen,
  IconPlay,
  LineSkeleton,
  Modal,
  Note,
  TableSkeleton,
  Thumb,
  fullName,
  useLatest,
  useTableStyles
} from './peopleUI'
import './people.css'

/*
 * A player's own posts, and the reactions each one collected.
 *
 * The API is untouched:
 *   GET  admin/users/posts/:uuid
 *   GET  admin/users/posts/ranking/:id
 *   GET  admin/users/posts/delete/:id
 *   POST admin/users/posts/addpoints
 *
 * Same treatment as the challenge list next to it: a poster frame per row
 * instead of a live video player per row, the clip and the reactions in the
 * dialog, and a delete that says what it is about to remove instead of a
 * browser confirm box.
 *
 * VIDEO — nothing here builds a stream url any more.
 *
 * The dialog used to mount a <ReactPlayer> on one hand-picked url in a fixed
 * 16/9 box. Every id now goes through videoSource and every clip plays in the
 * shared VideoModal, which strips the extension the API attaches to VideoID
 * ("OTH_123.mov" -> "OTH_123"), walks 1080_{id}.mp4 -> {id}.smil ->
 * 720_{id}.mp4 until one answers, and sizes its frame from the clip's own
 * dimensions so a portrait phone post is not letterboxed.
 *
 * The player gets the player's whole set of clips and a starting index, so a
 * reviewer moves through a user's posts inside it. Walking to another clip
 * moves the post underneath it too, so the points form and the reaction list
 * always belong to what is on screen.
 */

/*
 * The clip surface inside the post dialog: poster frame, one press to play.
 * Its twin lives in UserChallenges.js — the natural home is peopleUI, which
 * this task does not own.
 */
function PosterButton({ fileId, label, onClick }) {
  const [broken, setBroken] = useState(false)
  const src = posterUrl(fileId)

  return (
    <button type="button" className="pp-poster" onClick={onClick} title={label} aria-label={label}>
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
      ) : (
        <span className="pp-poster-none">Video</span>
      )}
      <span className="pp-poster-play" aria-hidden="true"><IconPlay /></span>
    </button>
  )
}

export default function UserPosts(props) {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState([])
  const [show, setShow] = useState(false)
  const [content, setContent] = useState({})
  const [reactions, setReactions] = useState([])

  const [failed, setFailed] = useState(false)
  const [reactionsLoading, setReactionsLoading] = useState(false)
  const [note, setNote] = useState(null)
  const [askDelete, setAskDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const [savingPoints, setSavingPoints] = useState(false)

  /* -1 = no clip open. Any other value indexes `clips` below, not `data`. */
  const [clipIndex, setClipIndex] = useState(-1)

  const points = useRef()
  const tableStyles = useTableStyles(false)

  /* The column array is memoised once, so handleShow() below closes over the
     first render's `data` — an empty array. It looks the row up through this
     ref, which always holds the rows currently on screen. */
  const dataRef = useLatest(data)

  /* The API hands the same id out under two names and in two shapes — VID is
     usually the clean "OTH_123", VideoID the same thing with the recorder's
     extension still on it. Whichever one a row carries, videoSource is what
     turns it into a poster; nothing here concatenates a path. */
  const fileIdOf = (row) => baseFileId((row && (row.VID || row.VideoID)) || '')

  const thumbFor = (row) => (
    row.Photo && row.Photo.length > 0
      ? process.env.REACT_APP_API_SERVER + 'postfiles/' + row.ContentUUID + '/123'
      : posterUrl(fileIdOf(row))
  )

  const columns = useMemo(() => [
    {
      name: '',
      cell: (row) => (
        <Thumb
          src={thumbFor(row)}
          label="Open this post"
          onClick={() => handleShow(row.id)}
        />
      ),
      width: '104px'
    },
    {
      name: 'Description',
      selector: (row) => row.Descr || '',
      cell: (row) => (
        <button
          type="button"
          className="pp-name pp-wrap"
          title="Open this post"
          onClick={() => handleShow(row.id)}
        >
          {row.Descr || 'Untitled post'}
        </button>
      ),
      sortable: true,
      minWidth: '240px',
      grow: 3,
      wrap: true
    },
    {
      name: 'Ranking',
      selector: (row) => Number(row.v) || 0,
      cell: (row) => <span className="pp-num">{row.v == null ? '—' : row.v}</span>,
      sortable: true,
      width: '110px',
      right: true
    },
    {
      name: 'Date created',
      selector: (row) => row.DateCreated || '',
      cell: (row) => (
        <span className="pp-mono-dim">
          {row.DateCreated ? moment(row.DateCreated).format('DD/MM/YYYY') : '—'}
        </span>
      ),
      sortable: true,
      width: '134px'
    },
    {
      name: '',
      cell: (row) => (
        <button
          type="button"
          className="pp-rowbtn"
          title="Open this post"
          aria-label="Open this post"
          onClick={() => handleShow(row.id)}
        >
          <i><IconOpen /></i>
        </button>
      ),
      width: '60px',
      center: true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  const reactionColumns = useMemo(() => [
    {
      name: 'Person who reacted',
      selector: (row) => fullName(row.FirstName, row.LastName),
      cell: (row) => (
        <div className="pp-face">
          <Avatar
            uuid={row.ReactUserID != '0' ? row.ReactUserUUID : undefined}
            name={fullName(row.FirstName, row.LastName)}
            size={28}
          />
          <span className="pp-cell-line">
            {row.ReactUserID == '0' ? 'Admin' : (fullName(row.FirstName, row.LastName) || '—')}
          </span>
        </div>
      ),
      sortable: true,
      minWidth: '200px',
      grow: 2
    },
    {
      name: 'Rank',
      selector: (row) => Number(row.RankType) || 0,
      cell: (row) => <span className="pp-num">{row.RankType}</span>,
      sortable: true,
      width: '90px',
      right: true
    }
  ], [])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/posts/' + props.id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const handleShow = (id) => {
    setReactionsLoading(true)
    setNote(null)
    setAskDelete(false)

    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/posts/ranking/' + id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setReactions(res.data.results)
        setReactionsLoading(false)
      }).catch(err => {
        setReactions([])
        setReactionsLoading(false)
        console.log(err)
      })

    setContent((dataRef.current || []).filter(item => item.id == id)[0] || {})
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setAskDelete(false)
  }

  const deleteContent = (id) => {
    setBusy(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/posts/delete/' + id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusy(false)
        if (res.data.err == 0) {
          setShow(false)
          setAskDelete(false)
          loadData()
          setNote(null)
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setBusy(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const addPoints = (e) => {
    e.preventDefault()
    setSavingPoints(true)

    const obj = {
      ChallengeID: content.id,
      ChUserID: content.PostedBy,
      ReactUserID: 0,
      RankValue: 1,
      RankType: points.current.value,
      PostType: 0,
    }

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/users/posts/addpoints', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSavingPoints(false)
        if (res.data.err == 0) {
          if (points.current) points.current.value = ''
          loadData()
          handleShow(content.id)
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setSavingPoints(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data])

  /* Every post of this player that carries a clip, in list order. The player
     receives this whole set plus a starting index, so the reviewer walks the
     user's videos inside it instead of reopening a dialog per clip. */
  const clips = useMemo(() => rows
    .map((row) => ({ row, fileId: fileIdOf(row) }))
    .filter((entry) => entry.fileId.length > 0)
    .map(({ row, fileId }) => ({
      id: row.id,
      fileId,
      name: props.name || 'This player',
      uuid: props.uuid || props.id,
      score: row.v == null || row.v === '' ? undefined : row.v,
      scoreLabel: 'rank',
      subtitle: row.Descr || 'Post'
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, props.name, props.uuid, props.id])

  const clipOpen = clipIndex >= 0 && clipIndex < clips.length

  /* A delete or a refresh can shorten the list under an open player. */
  useEffect(() => {
    if (clipIndex >= clips.length) setClipIndex(-1)
  }, [clips.length, clipIndex])

  /* The player opens on top of this screen's own dialog and both of them listen
     for Escape on `document`, so one press would otherwise close the clip and
     the post behind it together. While the player is up, Escape is the
     player's: this runs in the capture phase, stops the event dead, and closes
     the clip itself. */
  useEffect(() => {
    if (!clipOpen) return undefined

    const onKey = (ev) => {
      if (ev.key !== 'Escape' && ev.key !== 'Esc') return
      ev.stopImmediatePropagation()
      setClipIndex(-1)
    }

    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [clipOpen])

  const closeClip = useCallback(() => setClipIndex(-1), [])

  const openClip = (id) => {
    const at = clips.findIndex((item) => item.id == id)
    if (at >= 0) setClipIndex(at)
  }

  /* Moving to another clip moves the post under it as well, so the points form
     and the reactions never describe a different post than the one playing.
     The request is the one this screen already makes — same url, same shape. */
  const walkClip = (next) => {
    setClipIndex(next)
    const target = clips[next]
    if (target) handleShow(target.id)
  }

  const contentFileId = fileIdOf(content)
  const hasVideo = contentFileId.length > 0
  const hasPhoto = content.Photo && content.Photo.length > 0
  const clipPosition = clips.findIndex((item) => item.id == content.id)

  return (
    <Panel
      title="User Posts"
      subtitle={
        isLoading
          ? 'Loading…'
          : rows.length + (rows.length === 1 ? ' post' : ' posts')
      }
      actions={!isLoading && rows.length > 0 ? <Btn onClick={loadData}>Refresh</Btn> : null}
    >
      {note && !show ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {isLoading ? (
        <TableSkeleton rows={3} />
      ) : failed ? (
        <Empty
          title="Posts could not be loaded"
          action={<Btn variant="accent" onClick={loadData}>Try again</Btn>}
        >
          The request did not come back. Nothing has been lost.
        </Empty>
      ) : rows.length === 0 ? (
        <Empty title="No posts yet">
          Anything this player posts to their feed shows up here, with the
          reactions it collected.
        </Empty>
      ) : (
        <div className="pp-table">
          <DataTable
            columns={columns}
            data={rows}
            customStyles={tableStyles}
            highlightOnHover
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight="52vh"
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            noDataComponent={<Empty title="No posts" />}
          />
        </div>
      )}

      <Modal
        open={show}
        size="lg"
        title={content.Descr || 'Post'}
        subtitle={
          content.DateCreated
            ? 'Created ' + moment(content.DateCreated).format('DD/MM/YYYY')
            : undefined
        }
        onClose={handleClose}
      >
        {note ? (
          <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
        ) : null}

        {hasVideo ? (
          <div className="pp-media pp-media-clip">
            <PosterButton
              fileId={contentFileId}
              label="Play this clip"
              onClick={() => openClip(content.id)}
            />
            {clips.length > 1 && clipPosition >= 0 ? (
              <div className="pp-poster-hint">
                Clip {clipPosition + 1} of {clips.length} · the player walks the rest
              </div>
            ) : null}
          </div>
        ) : null}

        {hasPhoto ? (
          <div className="pp-media">
            <img
              className="pp-photo"
              src={process.env.REACT_APP_API_SERVER + 'postfiles/' + content.ContentUUID + '/123'}
              alt="Post"
            />
          </div>
        ) : null}

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Admin points</legend>
          <form className="pp-form" onSubmit={addPoints}>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="pp-po-points">Points</label>
              <input
                id="pp-po-points"
                className="pp-input"
                type="number"
                min={-100}
                step={1}
                max={100}
                ref={points}
                required
                placeholder='Enter Admin Points'
              />
              <div className="pp-hint">
                Between -100 and 100. The post keeps every reaction it already has —
                this is added on top as an admin reaction.
              </div>
            </div>
            <div className="pp-actions">
              <Btn variant="accent" type="submit" disabled={savingPoints}>
                {savingPoints ? 'Adding…' : 'Add points'}
              </Btn>
            </div>
          </form>
        </fieldset>

        <div className="pp-field">
          <div className="pp-label">
            Reactions{reactions && reactions.length ? ' · ' + reactions.length : ''}
          </div>
          {reactionsLoading ? (
            <LineSkeleton lines={3} />
          ) : !reactions || reactions.length === 0 ? (
            <Empty title="No reactions yet">
              Nobody has ranked this post.
            </Empty>
          ) : (
            <div className="pp-table">
              <DataTable
                columns={reactionColumns}
                data={reactions}
                customStyles={tableStyles}
                highlightOnHover
                persistTableHead
                fixedHeader
                fixedHeaderScrollHeight="34vh"
                noDataComponent={<Empty title="No reactions" />}
              />
            </div>
          )}
        </div>

        {askDelete ? (
          <Confirm
            title="Delete this post"
            confirmLabel="Delete post"
            busy={busy}
            onCancel={() => setAskDelete(false)}
            onConfirm={() => deleteContent(content.id)}
          >
            This post and the reactions on it are removed permanently. The player
            keeps their account. This cannot be undone.
          </Confirm>
        ) : (
          <div className="pp-actions">
            <button type="button" className="btn-ui pp-crit" onClick={() => setAskDelete(true)}>
              Delete this content
            </button>
          </div>
        )}
      </Modal>

      {clipOpen ? (
        <VideoModal
          items={clips}
          index={clipIndex}
          title={props.name ? props.name + ' · posts' : 'Posts'}
          onIndex={walkClip}
          onClose={closeClip}
        />
      ) : null}
    </Panel>
  )
}
